import { ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { NewBillingState } from 'src/app/pages/new-billing/store/state';
import { SubscriptionStatus } from 'src/app/pages/new-billing/types/api.types';
import { PaymentSummaryInfo } from 'src/app/pages/new-subscribe-plans/store/model';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { normalizePrice } from 'src/app/util/price-formatter.util';

@UntilDestroy()
@Component({
  selector: 'app-payment-summary',
  templateUrl: './payment-summary.component.html',
  styleUrls: ['./payment-summary.component.scss'],
})
export class PaymentSummaryComponent implements OnInit {
  @Input() inputData: PaymentSummaryInfo;
  isV2 = false;

  isLoadingAction: boolean;
  errorMsg: string;

  cardInfo: PaymentSummaryInfo['cardInfo'];
  planInfo: PaymentSummaryInfo['planInfo'];
  buttons: PaymentSummaryInfo['buttons'];

  data: PaymentSummaryInfo;
  planSelected: string;

  status: SubscriptionStatus;
  currentDate: Date = new Date();

  // Computed properties for template logic
  get shouldShowHeaderButtons(): boolean {
    return !this.isV2 && !!(this.buttons?.action || this.buttons?.dismiss);
  }

  get shouldShowFooterButtons(): boolean {
    return this.isV2 && !!(this.buttons?.action || this.buttons?.dismiss);
  }

  get titleDate(): string {
    return this.titleMap(this.status);
  }

  get paymentDate(): Date {
    return this.status === 'trial' ? this.data.nextPaymentDate as unknown as Date : new Date();
  }

  get paymentMethodTextKey() {
    return this.cardInfo ? 'billing.paymentMethod' : 'billing.updatePaymentMethod';
  }

  get iconName() {
    switch (this.dialogData.type) {
      case 'info': return 'info';
      case 'error': return 'error';
      case 'warning': return 'report_problem';
      default: return 'check_circle';
    }
  }

  get totalAmountLabel() {
    return this.data.buttons?.action?.text === 'common.upgrade'
      ? 'billing.paymentSummary.upgrade.totalAmount'
      : 'billing.paymentSummary.downgrade.totalAmount';
  }

  constructor(
    private translateService: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
    private store: Store,
    @Inject(MAT_DIALOG_DATA) public dialogData: PaymentSummaryInfo,
    private readonly localizedPricingService: LocalizedPricingService,
    private readonly segment: SegmentService,
  ) {}

  ngOnInit() {
    this.data = this.inputData ?? this.dialogData;
    const { cardInfo, planInfo, buttons, onCardUpdate } = this.data;
    this.isV2 = this.data?.isV2 ?? false;


    this.cardInfo = cardInfo;
    this.planInfo = planInfo;
    this.buttons = buttons;

    this.status = this.store.selectSnapshot(NewBillingState.updatedBillingDetails).customerStatus;

    this.planSelected = this.translateService.instant(`billing.${planInfo.name}`);
    onCardUpdate?.pipe(
      untilDestroyed(this),
    )?.subscribe(({ cardLast4 }) => {
      this.cardInfo.cardLastFour = cardLast4;
      this.changeDetectorRef.markForCheck();
    });

    this.trackConfirmPlanUpgradeModalViewed();
  }

  private trackConfirmPlanUpgradeModalViewed(): void {
    const { trackingContext } = this.data;
    if (!trackingContext) {
      return;
    }

    this.segment.trackInAppEvents('Confirm Plan Upgrade Modal Viewed', {
      upsellname: trackingContext.upsellname,
      linklocation: trackingContext.linklocation,
      targetPlan: trackingContext.targetPlan ?? this.planInfo?.name,
    });
  }

  titleMap(status: SubscriptionStatus) {
    return {
      paid: 'billing.paymentSummary.upcomingPaymentDate',
      trial: 'billing.nextPaymentDate',
      past_due: 'billing.date',
    }[status];
  }

  /**
   * Formats a price with localized currency for template usage
   * @param price - The price to format
   * @returns string - Formatted price with appropriate currency symbol
   */
  formatLocalizedPrice(price: number): string {
    const normalizedPrice = normalizePrice(price);
    const billingCurrency = this.store.selectSnapshot(NewBillingState.updatedBillingDetails)?.currency;
    const isEnabled = this.localizedPricingService.isLocalizedPricingEnabled();

    // Priority 1: Always respect billing currency if available
    if (isEnabled && billingCurrency) {
      return this.localizedPricingService.formatPriceWithCurrency(normalizedPrice, billingCurrency, 'excludePlanAndAddon');
    }

    // Priority 3: Use geo-based pricing only if localized pricing is enabled and no billing currency
    if (isEnabled && !billingCurrency) {
      return this.localizedPricingService.formatPriceWithCurrency(normalizedPrice, 'USD', 'excludePlanAndAddon');
    }

    // Priority 4: Fallback to USD
    return this.localizedPricingService.formatPriceWithCurrency(normalizedPrice, 'USD', 'excludePlanAndAddon');
  }
}
